import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '@/utils/axios';
import { toast } from 'sonner';

// Initial state
const initialState = {
  // User tickets
  userTickets: [],
  userTicketsLoading: false,
  userTicketsError: null,
  userTicketsPagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasMore: false,
  },

  // Current ticket (for ticket details)
  currentTicket: null,
  currentTicketLoading: false,
  currentTicketError: null,

  // Admin tickets
  adminTickets: [],
  adminTicketsLoading: false,
  adminTicketsError: null,
  adminTicketsPagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasMore: false,
  },

  // Ticket creation
  creatingTicket: false,
  createTicketError: null,

  // Filters (for admin)
  filters: {
    status: '',
    searchTerm: '',
  },
};

// ==================== USER TICKET THUNKS ====================

export const createTicket = createAsyncThunk(
  'tickets/createTicket',
  async (ticketData, { rejectWithValue, dispatch }) => {
    try {
      const res = await axios.post('/tickets', ticketData);
      toast.success('Ticket created successfully!');
      // Refresh user tickets after creation
      dispatch(getUserTickets({ page: 1, limit: 10 }));
      return res.data;
    } catch (error) {
      console.error('Failed to create ticket', error);
      const message = error?.response?.data?.message || 'Ticket creation failed.';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const getUserTickets = createAsyncThunk(
  'tickets/getUserTickets',
  async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const res = await axios.get('/tickets', {
        params: { page, limit },
      });
      return res.data; // Returns { tickets, pagination }
    } catch (error) {
      console.error('Failed to fetch tickets', error);
      const message = error.response?.data?.message || 'Failed to fetch tickets';
      toast.error('Unable to load tickets.');
      return rejectWithValue(message);
    }
  }
);

export const getTicketById = createAsyncThunk(
  'tickets/getTicketById',
  async (ticketId, { rejectWithValue }) => {
    try {
      const res = await axios.get(`/tickets/${ticketId}`);
      return res.data; // Returns { ticket, messages }
    } catch (error) {
      console.error('Failed to fetch ticket', error);
      const message = error.response?.data?.message || 'Failed to fetch ticket';
      return rejectWithValue(message);
    }
  }
);

export const getTicketUpdates = createAsyncThunk(
  'tickets/getTicketUpdates',
  async (ticketId, { rejectWithValue }) => {
    try {
      const res = await axios.get(`/tickets/${ticketId}/updates`);
      return res.data;
    } catch (error) {
      console.error('Failed to fetch ticket updates', error);
      const message = error.response?.data?.message || 'Failed to fetch ticket updates';
      return rejectWithValue(message);
    }
  }
);

export const replyToTicket = createAsyncThunk(
  'tickets/replyToTicket',
  async ({ ticketId, message, parentMessageId = null }, { rejectWithValue, dispatch }) => {
    try {
      const res = await axios.post(`/tickets/${ticketId}/reply`, {
        message,
        parent_message_id: parentMessageId,
      });
      toast.success('Reply sent successfully.');
      // Refresh ticket details after reply
      dispatch(getTicketById(ticketId));
      return res.data;
    } catch (error) {
      console.error('Failed to reply to ticket', error);
      const message = error?.response?.data?.message || 'Failed to send reply.';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const deleteTicket = createAsyncThunk(
  'tickets/deleteTicket',
  async (ticketId, { rejectWithValue, dispatch, getState }) => {
    try {
      const res = await axios.delete(`/tickets/${ticketId}`);
      toast.success('Ticket deleted successfully.');
      // Refresh user tickets after deletion
      const state = getState();
      const currentPage = state.tickets.userTicketsPagination.page;
      dispatch(getUserTickets({ page: currentPage, limit: 10 }));
      return res.data;
    } catch (error) {
      console.error('Failed to delete ticket', error);
      const message = error?.response?.data?.message || 'Ticket deletion failed.';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// ==================== ADMIN TICKET THUNKS ====================

export const getAllTickets = createAsyncThunk(
  'tickets/getAllTickets',
  async ({ page = 1, limit = 10, filters = {} }, { rejectWithValue }) => {
    try {
      const res = await axios.get('/admin/tickets', {
        params: {
          page,
          limit,
          ...filters,
        },
      });
      return res.data; // Returns { tickets, pagination, filters }
    } catch (error) {
      console.error('Failed to fetch all tickets', error);
      const message = error.response?.data?.message || 'Failed to fetch tickets';
      toast.error('Failed to fetch tickets.');
      return rejectWithValue(message);
    }
  }
);

export const getTicketWithReplies = createAsyncThunk(
  'tickets/getTicketWithReplies',
  async (ticketId, { rejectWithValue }) => {
    try {
      const res = await axios.get(`/admin/tickets/${ticketId}`);
      return res.data; // Returns { ticket, messages }
    } catch (error) {
      console.error('Failed to fetch ticket details', error);
      const message = error?.response?.data?.message || 'Failed to fetch ticket details.';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const replyToTicketAsAdmin = createAsyncThunk(
  'tickets/replyToTicketAsAdmin',
  async ({ ticketId, message }, { rejectWithValue, dispatch }) => {
    try {
      const res = await axios.post(`/admin/tickets/${ticketId}/reply`, { message });
      toast.success('Reply sent successfully.');
      // Refresh ticket details after reply
      dispatch(getTicketWithReplies(ticketId));
      return res.data;
    } catch (error) {
      console.error('Failed to reply to ticket', error);
      const message = error?.response?.data?.message || 'Failed to send reply.';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const updateTicketStatus = createAsyncThunk(
  'tickets/updateTicketStatus',
  async ({ ticketId, status }, { rejectWithValue, dispatch, getState }) => {
    try {
      const res = await axios.put(`/admin/tickets/${ticketId}/status`, { status });
      toast.success('Ticket status updated successfully.');
      // Refresh admin tickets after status update
      const state = getState();
      const currentPage = state.tickets.adminTicketsPagination.page;
      const filters = state.tickets.filters;
      dispatch(getAllTickets({ 
        page: currentPage, 
        limit: 10, 
        filters: {
          status: filters.status || undefined,
          search: filters.searchTerm || undefined,
        }
      }));
      return { ticketId, status, ticket: res.data };
    } catch (error) {
      console.error('Failed to update ticket status', error);
      const message = error?.response?.data?.message || 'Failed to update ticket status.';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const deleteTicketAsAdmin = createAsyncThunk(
  'tickets/deleteTicketAsAdmin',
  async (ticketId, { rejectWithValue, dispatch, getState }) => {
    try {
      const res = await axios.delete(`/admin/tickets/${ticketId}`);
      toast.success('Ticket deleted successfully.');
      // Refresh admin tickets after deletion
      const state = getState();
      const currentPage = state.tickets.adminTicketsPagination.page;
      const filters = state.tickets.filters;
      dispatch(getAllTickets({ 
        page: currentPage, 
        limit: 10, 
        filters: {
          status: filters.status || undefined,
          search: filters.searchTerm || undefined,
        }
      }));
      return res.data;
    } catch (error) {
      console.error('Failed to delete ticket', error);
      const message = error?.response?.data?.message || 'Ticket deletion failed.';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Ticket slice
const ticketSlice = createSlice({
  name: 'tickets',
  initialState,
  reducers: {
    // Clear current ticket
    clearCurrentTicket: (state) => {
      state.currentTicket = null;
      state.currentTicketError = null;
    },

    // Filters (for admin)
    setStatusFilter: (state, action) => {
      state.filters.status = action.payload;
      state.adminTicketsPagination.page = 1;
    },
    setSearchTerm: (state, action) => {
      state.filters.searchTerm = action.payload;
      state.adminTicketsPagination.page = 1;
    },
    clearFilters: (state) => {
      state.filters = {
        status: '',
        searchTerm: '',
      };
      state.adminTicketsPagination.page = 1;
    },

    // Pagination
    setUserTicketsPage: (state, action) => {
      state.userTicketsPagination.page = action.payload;
    },
    setAdminTicketsPage: (state, action) => {
      state.adminTicketsPagination.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Create ticket
    builder
      .addCase(createTicket.pending, (state) => {
        state.creatingTicket = true;
        state.createTicketError = null;
      })
      .addCase(createTicket.fulfilled, (state) => {
        state.creatingTicket = false;
        state.createTicketError = null;
      })
      .addCase(createTicket.rejected, (state, action) => {
        state.creatingTicket = false;
        state.createTicketError = action.payload;
      });

    // Get user tickets
    builder
      .addCase(getUserTickets.pending, (state) => {
        state.userTicketsLoading = true;
        state.userTicketsError = null;
      })
      .addCase(getUserTickets.fulfilled, (state, action) => {
        state.userTicketsLoading = false;
        state.userTickets = action.payload.tickets || action.payload || [];
        if (action.payload.pagination) {
          state.userTicketsPagination = {
            ...state.userTicketsPagination,
            ...action.payload.pagination,
          };
        }
        state.userTicketsError = null;
      })
      .addCase(getUserTickets.rejected, (state, action) => {
        state.userTicketsLoading = false;
        state.userTicketsError = action.payload;
        state.userTickets = [];
      });

    // Get ticket by ID
    builder
      .addCase(getTicketById.pending, (state) => {
        state.currentTicketLoading = true;
        state.currentTicketError = null;
      })
      .addCase(getTicketById.fulfilled, (state, action) => {
        state.currentTicketLoading = false;
        state.currentTicket = action.payload;
        state.currentTicketError = null;
      })
      .addCase(getTicketById.rejected, (state, action) => {
        state.currentTicketLoading = false;
        state.currentTicketError = action.payload;
        state.currentTicket = null;
      });

    // Reply to ticket
    builder
      .addCase(replyToTicket.fulfilled, (state, action) => {
        // Ticket details will be refreshed via getTicketById
        // Update in user tickets list if needed
        if (state.currentTicket && state.currentTicket.ticket?.ticket_id === action.payload.ticketId) {
          if (!state.currentTicket.messages) {
            state.currentTicket.messages = [];
          }
          state.currentTicket.messages.push(action.payload);
        }
      });

    // Delete ticket
    builder
      .addCase(deleteTicket.fulfilled, (state, action) => {
        state.userTickets = state.userTickets.filter(
          (ticket) => ticket.ticket_id !== action.payload.ticket_id && ticket.id !== action.payload.id
        );
        if (state.currentTicket?.ticket?.ticket_id === action.payload.ticket_id || 
            state.currentTicket?.ticket?.id === action.payload.id) {
          state.currentTicket = null;
        }
      });

    // Get all tickets (admin)
    builder
      .addCase(getAllTickets.pending, (state) => {
        state.adminTicketsLoading = true;
        state.adminTicketsError = null;
      })
      .addCase(getAllTickets.fulfilled, (state, action) => {
        state.adminTicketsLoading = false;
        state.adminTickets = action.payload.tickets || [];
        if (action.payload.pagination) {
          state.adminTicketsPagination = {
            ...state.adminTicketsPagination,
            ...action.payload.pagination,
          };
        }
        state.adminTicketsError = null;
      })
      .addCase(getAllTickets.rejected, (state, action) => {
        state.adminTicketsLoading = false;
        state.adminTicketsError = action.payload;
        state.adminTickets = [];
      });

    // Get ticket with replies (admin)
    builder
      .addCase(getTicketWithReplies.pending, (state) => {
        state.currentTicketLoading = true;
        state.currentTicketError = null;
      })
      .addCase(getTicketWithReplies.fulfilled, (state, action) => {
        state.currentTicketLoading = false;
        state.currentTicket = action.payload;
        state.currentTicketError = null;
      })
      .addCase(getTicketWithReplies.rejected, (state, action) => {
        state.currentTicketLoading = false;
        state.currentTicketError = action.payload;
        state.currentTicket = null;
      });

    // Update ticket status (admin)
    builder
      .addCase(updateTicketStatus.fulfilled, (state, action) => {
        const { ticketId, status } = action.payload;
        // Update in admin tickets
        const adminTicketIndex = state.adminTickets.findIndex(
          (t) => t.ticket_id === ticketId || t.id === ticketId
        );
        if (adminTicketIndex !== -1) {
          state.adminTickets[adminTicketIndex].status = status;
        }
        // Update current ticket if it's the same
        if (state.currentTicket?.ticket?.ticket_id === ticketId || 
            state.currentTicket?.ticket?.id === ticketId) {
          state.currentTicket.ticket.status = status;
        }
      });

    // Delete ticket as admin
    builder
      .addCase(deleteTicketAsAdmin.fulfilled, (state, action) => {
        state.adminTickets = state.adminTickets.filter(
          (ticket) => ticket.ticket_id !== action.payload.ticket_id && ticket.id !== action.payload.id
        );
        if (state.currentTicket?.ticket?.ticket_id === action.payload.ticket_id || 
            state.currentTicket?.ticket?.id === action.payload.id) {
          state.currentTicket = null;
        }
      });
  },
});

export const {
  clearCurrentTicket,
  setStatusFilter,
  setSearchTerm,
  clearFilters,
  setUserTicketsPage,
  setAdminTicketsPage,
} = ticketSlice.actions;

// Selectors
export const selectUserTickets = (state) => state.tickets.userTickets;
export const selectUserTicketsLoading = (state) => state.tickets.userTicketsLoading;
export const selectUserTicketsError = (state) => state.tickets.userTicketsError;
export const selectUserTicketsPagination = (state) => state.tickets.userTicketsPagination;

export const selectCurrentTicket = (state) => state.tickets.currentTicket;
export const selectCurrentTicketLoading = (state) => state.tickets.currentTicketLoading;
export const selectCurrentTicketError = (state) => state.tickets.currentTicketError;

export const selectAdminTickets = (state) => state.tickets.adminTickets;
export const selectAdminTicketsLoading = (state) => state.tickets.adminTicketsLoading;
export const selectAdminTicketsError = (state) => state.tickets.adminTicketsError;
export const selectAdminTicketsPagination = (state) => state.tickets.adminTicketsPagination;

export const selectCreatingTicket = (state) => state.tickets.creatingTicket;
export const selectCreateTicketError = (state) => state.tickets.createTicketError;

export const selectTicketFilters = (state) => state.tickets.filters;

export default ticketSlice.reducer;

